import { useState, type FormEvent } from "react";
import { Check, Copy, UserPlus } from "lucide-react";
import { C, font } from "../theme";
import { FieldInput } from "./Field";
import { Button } from "./Button";
import { Select } from "./Select";
import { useSession } from "../lib/session/SessionContext";
import { createTeamInvite } from "../lib/api/endpoints";
import { ApiError } from "../lib/api/client";
import { TEAM_ROLES, type TeamRole } from "../lib/roles";
import { roleLabel } from "../lib/teamRoles";
import type { InviteDto } from "../types";

interface TeamInvitePanelProps {
  roles?: TeamRole[];
  onInvite?: (invite: InviteDto) => void;
  /** Oculta a lista local de convites gerados (a fonte passa a ser a API). */
  hideInvites?: boolean;
}

/** Formulário de convite de funcionário (e-mail + papel) com lista dos links gerados. */
export function TeamInvitePanel({ roles = TEAM_ROLES, onInvite, hideInvites = false }: TeamInvitePanelProps) {
  const { accessToken } = useSession();
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState(roles[0]!.id);
  const [invites, setInvites] = useState<InviteDto[]>([]);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addEmployee = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email) {
      setError("Informe o e-mail do funcionário.");
      return;
    }
    if (!accessToken) return;
    setBusy(true);
    try {
      const invite = await createTeamInvite(accessToken, email, roleId);
      setInvites(prev => [invite, ...prev]);
      setEmail("");
      onInvite?.(invite);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) setError(err.detail ?? "Já existe um convite pendente ou o usuário já é membro deste CT.");
        else setError(err.detail ?? "Não foi possível gerar o convite.");
      } else {
        setError(err instanceof Error ? err.message : "Não foi possível gerar o convite.");
      }
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async (code: string) => {
    const link = `${window.location.origin}/invite/${code}`;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // clipboard pode não estar disponível fora de contexto seguro; o link fica visível
    }
    setCopiedCode(code);
    window.setTimeout(() => setCopiedCode(null), 2000);
  };

  const roleOptions = roles.map(r => ({ value: r.id, label: r.label }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <form
        onSubmit={addEmployee}
        style={{ display: "flex", flexDirection: "column", gap: 12, background: C.bg, border: `1px solid ${C.faint}`, borderRadius: 12, padding: 16 }}
      >
        <div style={{ fontSize: 11, letterSpacing: 1, color: C.gray, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
          <UserPlus size={13} /> NOVO FUNCIONÁRIO
        </div>
        <label style={{ fontSize: 11, letterSpacing: 1, color: C.gray, fontWeight: 600 }}>
          E-MAIL
          <FieldInput type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="funcionario@ct.com" style={{ marginTop: 4 }} />
        </label>
        <label style={{ fontSize: 11, letterSpacing: 1, color: C.gray, fontWeight: 600 }}>
          PAPEL
          <Select id="team-invite-role" value={roleId} options={roleOptions} onChange={setRoleId} style={{ marginTop: 4 }} ariaLabel="Papel do funcionário" />
        </label>
        {error && (
          <div style={{ fontSize: 12.5, color: C.red, background: "C.redSoft", borderRadius: 8, padding: "8px 12px" }}>
            {error}
          </div>
        )}
        <Button type="submit" disabled={busy} style={{ fontSize: 13, padding: "10px 14px" }}>
          {busy ? "Gerando convite..." : "Gerar convite"}
        </Button>
      </form>

      {!hideInvites && invites.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 11, letterSpacing: 1, color: C.gray, fontWeight: 600 }}>CONVITES GERADOS</div>
          {invites.map(inv => (
            <div key={inv.id} style={{ display: "flex", flexDirection: "column", gap: 6, border: `1px solid ${C.faint}`, borderRadius: 10, padding: "10px 12px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: C.ink }}>{inv.email}</span>
                <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, color: C.ink, background: C.limeSoft, padding: "3px 9px", borderRadius: 20, textTransform: "uppercase" }}>
                  {roleLabel(inv.roleId)}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <code style={{ flex: 1, fontSize: 11.5, color: C.gray, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {window.location.origin}/invite/{inv.code}
                </code>
                <button
                  type="button"
                  onClick={() => copyLink(inv.code)}
                  aria-label={`Copiar link de ${inv.email}`}
                  style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "none", cursor: "pointer", color: C.ink, fontFamily: font, fontSize: 11.5, fontWeight: 600, padding: 4 }}
                >
                  {copiedCode === inv.code ? <Check size={14} style={{ color: C.ink }} /> : <Copy size={14} />}
                  {copiedCode === inv.code ? "Copiado" : "Copiar"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}